// ─── Service Bus CRUD resource & action constants ──────────────────────────

export const SERVICE_BUS_CRUD_RESOURCES = {
  ENDPOINT_CONFIG: 'endpoint-config',
  ORDER: 'order',
  WEBHOOK_CONFIG: 'webhook-config',
} as const;
export type ServiceBusCrudResourceName = (typeof SERVICE_BUS_CRUD_RESOURCES)[keyof typeof SERVICE_BUS_CRUD_RESOURCES];

export const SERVICE_BUS_CRUD_ACTIONS = { CREATE: 'create', DELETE: 'delete', UPDATE: 'update' } as const;
export type ServiceBusCrudAction = (typeof SERVICE_BUS_CRUD_ACTIONS)[keyof typeof SERVICE_BUS_CRUD_ACTIONS];

/**
 * Derives a CRUD request topic name from a resource and an action, e.g.
 * ('endpoint-config', 'create') -> 'endpoint-config-create-request'. Generic
 * parameters preserve literal string types so callers get a literal return
 * type instead of a widened `string`.
 */
export function getCrudRequestTopicName<Resource extends string, Action extends ServiceBusCrudAction>(
  resource: Resource,
  action: Action,
): `${Resource}-${Action}-request` {
  return `${resource}-${action}-request`;
}

export const SERVICE_BUS_TOPICS = {
  CALL_REQUEST: 'on-call-request',
  // ─── Carrier sync topics ────────────────────────────────────────────
  CARRIER_CREATED: 'on-carrier-created',
  CARRIER_DELETED: 'on-carrier-deleted',
  CARRIER_UPDATED: 'on-carrier-updated',
  EMAIL_SEND_REQUESTED: 'on-email-send-requested',
  EMAIL_SEND_RESPONSE: 'on-email-send-response',
  EMAIL_STATUS_UPDATED: 'on-email-status-updated',
  // ─── Endpoint config provisioning topics ────────────────────────────
  ENDPOINT_CONFIG_CREATE_REQUEST: getCrudRequestTopicName(
    SERVICE_BUS_CRUD_RESOURCES.ENDPOINT_CONFIG,
    SERVICE_BUS_CRUD_ACTIONS.CREATE,
  ),
  ENDPOINT_CONFIG_DELETE_REQUEST: getCrudRequestTopicName(
    SERVICE_BUS_CRUD_RESOURCES.ENDPOINT_CONFIG,
    SERVICE_BUS_CRUD_ACTIONS.DELETE,
  ),
  ENDPOINT_CONFIG_UPDATE_REQUEST: getCrudRequestTopicName(
    SERVICE_BUS_CRUD_RESOURCES.ENDPOINT_CONFIG,
    SERVICE_BUS_CRUD_ACTIONS.UPDATE,
  ),
  // ─── ETA topics ──────────────────────────────────────────────────────
  ETA_COMPUTED: 'on-eta-computed',
  GEOCODING_REQUEST: 'on-geocoding-request',
  GEOCODING_RESPONSE: 'on-geocoding-response',
  // ─── HandlingUnitType sync topics ───────────────────────────────────
  HANDLING_UNIT_TYPE_CREATED: 'on-handling-unit-type-created',
  HANDLING_UNIT_TYPE_DELETED: 'on-handling-unit-type-deleted',
  HANDLING_UNIT_TYPE_UPDATED: 'on-handling-unit-type-updated',
  // ─── Import topics ───────────────────────────────────────────────────
  IMPORT_COMPLETED: 'on-import-completed',
  // ─── KPI topics ──────────────────────────────────────────────────────
  KPI_COMPUTED: 'on-kpi-computed',
  KPI_COMPUTE_REQUEST: 'kpi-compute-request',
  KPI_REFRESH_TICK: 'kpi-refresh-tick',
  // ─── Order topics ───────────────────────────────────────────────────
  ORDER_CREATED: 'on-order-created',
  // ─── Order import provisioning topics ───────────────────────────────
  ORDER_CREATE_REQUEST: getCrudRequestTopicName(SERVICE_BUS_CRUD_RESOURCES.ORDER, SERVICE_BUS_CRUD_ACTIONS.CREATE),
  ORDER_DELETED: 'on-order-deleted',
  ORDER_INGEST_REFUSED: 'on-order-ingest-refused',
  ORDER_UPDATE_REQUEST: getCrudRequestTopicName(SERVICE_BUS_CRUD_RESOURCES.ORDER, SERVICE_BUS_CRUD_ACTIONS.UPDATE),
  ORDER_UPDATED: 'on-order-updated',
  // ─── OrderType sync topics ──────────────────────────────────────────
  ORDER_TYPE_CREATED: 'on-order-type-created',
  ORDER_TYPE_DELETED: 'on-order-type-deleted',
  ORDER_TYPE_UPDATED: 'on-order-type-updated',
  // ─── Routing topics ──────────────────────────────────────────────────
  ROUTING_REQUEST: 'on-routing-request',
  ROUTING_RESPONSE: 'on-routing-response',
  SITE_CREATED: 'on-site-created',
  // ─── Site sync topics ───────────────────────────────────────────────
  SITE_DELETED: 'on-site-deleted',
  SITE_UPDATED: 'on-site-updated',
  // ─── Task sync topics ───────────────────────────────────────────────
  TASK_CREATED: 'on-task-created',
  TASK_DELETED: 'on-task-deleted',
  TASK_UPDATED: 'on-task-updated',
  TEMPLATE_PROVISIONING_RESULT: 'on-template-provisioning-result',
  // ─── Tenant sync topics ─────────────────────────────────────────────
  TENANT_CREATED: 'on-tenant-created',
  TENANT_DELETED: 'on-tenant-deleted',
  TENANT_UPDATED: 'on-tenant-updated',
  // ─── Tenant default sync topics ─────────────────────────────────────────────
  TENANT_DEFAULT_CREATED: 'on-tenant-default-created',
  TENANT_DEFAULT_DELETED: 'on-tenant-default-deleted',
  TENANT_DEFAULT_UPDATED: 'on-tenant-default-updated',
  // ─── Site update command topic ──────────────────────────────────────
  UPDATE_SITE: 'update-site',
  // ─── Webhook config provisioning topics ─────────────────────────────
  WEBHOOK_CONFIG_CREATE_REQUEST: getCrudRequestTopicName(
    SERVICE_BUS_CRUD_RESOURCES.WEBHOOK_CONFIG,
    SERVICE_BUS_CRUD_ACTIONS.CREATE,
  ),
  WEBHOOK_CONFIG_DELETE_REQUEST: getCrudRequestTopicName(
    SERVICE_BUS_CRUD_RESOURCES.WEBHOOK_CONFIG,
    SERVICE_BUS_CRUD_ACTIONS.DELETE,
  ),
  WEBHOOK_CONFIG_UPDATE_REQUEST: getCrudRequestTopicName(
    SERVICE_BUS_CRUD_RESOURCES.WEBHOOK_CONFIG,
    SERVICE_BUS_CRUD_ACTIONS.UPDATE,
  ),
} as const;
